// src/components/TrialCountdown.js
import React, { useEffect, useRef, useState } from 'react';
import { FaHourglassHalf, FaExclamationTriangle, FaLock } from 'react-icons/fa';
import { countdownTo, formatDateTimeSP } from '../utils/datetime';

/* ─── Paleta do BLB (navy/orange) + estados por urgência ─────────────────────
 * Verde   → mais de 24h restantes
 * Âmbar   → menos de 24h
 * Vermelho→ menos de 1h ou expirado
 * ─────────────────────────────────────────────────────────────────────────── */
function urgency(totalSec, expired) {
  if (expired) return { key: 'expired', accent: '#dc2626', soft: '#fff1f0', text: '#991b1b' };
  if (totalSec < 3600) return { key: 'critical', accent: '#dc2626', soft: '#fff1f0', text: '#991b1b' };
  if (totalSec < 86400) return { key: 'warning', accent: '#c2410c', soft: '#fff7ed', text: '#9a3412' };
  return { key: 'ok', accent: '#047857', soft: '#ecfdf5', text: '#065f46' };
}

const pad = (n) => String(n).padStart(2, '0');

/**
 * Props:
 *  - expiresAt   (string ISO UTC | Date | number)  instante de expiração
 *  - onExpire    (fn)   chamado UMA vez quando o contador zera
 *  - compact     (bool) variante para topbar/layout
 *  - label       (string) rótulo (default "Período de teste")
 *  - tickWhenExpired (bool) mantém render de expirado (default true)
 */
export default function TrialCountdown({
  expiresAt,
  onExpire,
  compact = false,
  label = 'Período de teste',
}) {
  const [cd, setCd] = useState(() => countdownTo(expiresAt));
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
    setCd(countdownTo(expiresAt));
    if (!expiresAt) return undefined;

    const tick = () => {
      const next = countdownTo(expiresAt);
      setCd(next);
      if (next?.expired && !firedRef.current) {
        firedRef.current = true;
        onExpire?.();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, onExpire]);

  if (!expiresAt || !cd) {
    return compact ? null : (
      <div style={{
        fontSize: 12, color: '#94a3b8', background: '#f8fafc',
        border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', fontStyle: 'italic',
      }}>
        Nenhuma data de teste definida.
      </div>
    );
  }

  const u = urgency(cd.totalSec, cd.expired);
  const alvoSP = formatDateTimeSP(expiresAt);

  /* ── Variante compacta (badge de layout) ─────────────────────────────────── */
  if (compact) {
    return (
      <div
        title={cd.expired ? 'Período de teste encerrado' : `Expira em ${alvoSP} (horário de Brasília)`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: u.soft, color: u.text,
          border: `1px solid ${u.accent}33`,
          borderRadius: 999, padding: '5px 12px',
          fontSize: 12, fontWeight: 700, letterSpacing: .2,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {cd.expired ? <FaLock size={11} color={u.accent} /> : <FaHourglassHalf size={11} color={u.accent} />}
        {cd.expired ? (
          <span>Teste encerrado</span>
        ) : (
          <span>
            Teste: {cd.days > 0 && `${cd.days}d `}{pad(cd.hours)}:{pad(cd.minutes)}:{pad(cd.seconds)}
          </span>
        )}
      </div>
    );
  }

  /* ── Variante completa (card) ────────────────────────────────────────────── */
  const Block = ({ value, unit }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 58 }}>
      <div style={{
        fontSize: 30, fontWeight: 900, lineHeight: 1, color: u.accent,
        fontVariantNumeric: 'tabular-nums', letterSpacing: -1,
      }}>
        {pad(value)}
      </div>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: '#94a3b8', marginTop: 6 }}>
        {unit}
      </div>
    </div>
  );
  const Sep = () => (
    <div style={{ fontSize: 26, fontWeight: 800, color: '#cbd5e1', alignSelf: 'flex-start', marginTop: 2 }}>:</div>
  );

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${u.accent}`,
      borderLeft: `4px solid ${u.accent}`,
      borderRadius: 12,
      padding: '18px 20px',
      boxShadow: '0 1px 4px rgba(0,0,0,.05)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
        fontSize: 10, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: '#64748b',
      }}>
        {cd.expired
          ? <FaExclamationTriangle size={12} color={u.accent} />
          : <FaHourglassHalf size={12} color={u.accent} />}
        {label}
      </div>

      {cd.expired ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: u.soft, borderRadius: 10, padding: '14px 16px',
          color: u.text, fontWeight: 700, fontSize: 14,
        }}>
          <FaLock size={16} color={u.accent} />
          Período de teste encerrado — o acesso está bloqueado.
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <Block value={cd.days} unit="dias" />
          <Sep />
          <Block value={cd.hours} unit="horas" />
          <Sep />
          <Block value={cd.minutes} unit="min" />
          <Sep />
          <Block value={cd.seconds} unit="seg" />
        </div>
      )}

      <div style={{ marginTop: 14, fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: '#94a3b8' }}>Expira em:</span>
        <strong style={{ color: '#0f172a' }}>{alvoSP}</strong>
        <span style={{ color: '#94a3b8' }}>(horário de Brasília)</span>
      </div>
    </div>
  );
}